import { Action, createReducer, on } from "@ngrx/store";

import * as DeliveriesActionTypes from "../../actions/deliveries/deliveries.actions";
import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";
import { InventoryMovementExtendedDTO } from "src/app/models/Deliveries/deliveriesDTO";

export interface DeliveriesState {
  deliveryList?: InventoryMovementExtendedDTO[];
  measurementList?: InventoryMovementExtendedDTO[];
  deliveryExcelList?: InventoryMovementExtendedDTO[];
  measurementExcelList?: InventoryMovementExtendedDTO[];
  debitList?: InventoryMovementExtendedDTO[];
  debitExcelList?: InventoryMovementExtendedDTO[];
  totalDeliveries?: number;
  totalMeasurements?: number;
  totalDebits?: number;
  isLoading: boolean;
  error: Error;
  created: boolean;
  delivery: any;
  stations?: any[];
  tanks?: any[];
  suppliers?: any[];
  totalRecords: number;
  pageReq: PaginationRequest;
  product: any;
  deliveryTableConfig: ColumnSorterInfo[];
  measurementTableConfig: ColumnSorterInfo[];
  debitTableConfig: ColumnSorterInfo[];
}

export const initialState: DeliveriesState = {
  deliveryList: null,
  measurementList: null,
  deliveryExcelList: null,
  measurementExcelList: null,
  debitList: null,
  debitExcelList: null,
  totalDebits: 0,
  totalDeliveries: null,
  isLoading: false,
  error: null,
  created: false,
  delivery: null,
  totalRecords: 0,
  totalMeasurements: 0,
  pageReq: null,
  product: null,
  deliveryTableConfig: null,
  measurementTableConfig: null,
  debitTableConfig: null,
};

const deliveriesReducer = createReducer(
  initialState,
  on(DeliveriesActionTypes.resetDeliveriesState, () => initialState),
  on(
    DeliveriesActionTypes.getDeliveries,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadDeliveries, (state, { response }) => {
    return {
      ...state,
      deliveryList: (response as any).result?.items
        ? (response as any).result?.items
        : [],
      error: null,
      isLoading: false,
      totalDeliveries: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(
    DeliveriesActionTypes.getMeasurements,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadMeasurements, (state, { response }) => {
    return {
      ...state,
      measurementList: (response as any).result?.items
        ? (response as any).result?.items
        : [],
      error: null,
      isLoading: false,
      totalMeasurements: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(
    DeliveriesActionTypes.getProduct,
    (state, { warehouseCode }) =>
      ({
        ...state,
        error: null,
        action: warehouseCode,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadProduct, (state, { response }) => {
    return {
      ...state,
      product: (response as any).result?.items
        ? (response as any).result?.items
        : undefined,
      error: null,
    };
  }),
  on(
    DeliveriesActionTypes.getDelivery,
    (state, { delivery }) =>
      ({
        ...state,
        error: null,
        action: delivery,
        created: false,
        isLoading: true,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadDelivery, (state, { response }) => {
    return {
      ...state,
      delivery: (response as any).result ? (response as any).result : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(DeliveriesActionTypes.setDelivery, (state, { delivery }) => ({
    ...state,
    delivery: delivery?.result || null,
    error: null,
  })),
  on(DeliveriesActionTypes.loadDeliveriesLoadStart, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(DeliveriesActionTypes.loadDeliveriesLoadEnd, (state) => ({
    ...state,
    isLoading: false,
    error: null,
  })),
  on(DeliveriesActionTypes.resetDelivery, (state) => {
    return {
      ...state,
      delivery: null,
      error: null,
    };
  }),
  on(DeliveriesActionTypes.resetMeasurement, (state) => {
    return {
      ...state,
      delivery: null,
      error: null,
    };
  }),
  on(
    DeliveriesActionTypes.getDeliveriesExcel,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadDeliveriesExcel, (state, { response }) => {
    return {
      ...state,
      deliveryExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    DeliveriesActionTypes.getMeasurementsExcel,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadMeasurementsExcel, (state, { response }) => {
    return {
      ...state,
      measurementExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    DeliveriesActionTypes.getDebitExcel,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadDebitExcel, (state, { response }) => {
    return {
      ...state,
      debitExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(
    DeliveriesActionTypes.getDebits,
    (state, { options }) =>
      ({
        ...state,
        error: null,
        options: options,
        isLoading: true,
      } as DeliveriesState)
  ),
  on(DeliveriesActionTypes.loadDebits, (state, { response }) => {
    return {
      ...state,
      debitList: (response as any).result?.items
        ? (response as any).result?.items
        : [],
      error: null,
      isLoading: false,
      totalDebits: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
    };
  }),
  on(DeliveriesActionTypes.loadDeliveriesTableConfig, (state, { response }) => {
    return {
      ...state,
      deliveryTableConfig:
        (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(DeliveriesActionTypes.loadMeasurementsTableConfig, (state, { response }) => {
    return {
      ...state,
      measurementTableConfig:
        (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(DeliveriesActionTypes.loadDebitsTableConfig, (state, { response }) => {
    return {
      ...state,
      debitTableConfig:
        (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(DeliveriesActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    };
  }),
  on(
    DeliveriesActionTypes.createDelivery,
    DeliveriesActionTypes.updateDelivery,
    DeliveriesActionTypes.saveTableConfig,
    DeliveriesActionTypes.getDeliveriesTableConfig,
    DeliveriesActionTypes.getMeasurementsTableConfig,
    DeliveriesActionTypes.getDebitsTableConfig,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  )
);

export function reducer(state: DeliveriesState | undefined, action: Action) {
  return deliveriesReducer(state, action);
}
